// Op channel for group delta: entry-level void verdict flips between start and end
// view horizons. Streams the same walk as the row channel; retains only flips.
// Each flip may carry a structured void reason at the voided horizon.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { isRefAdvancePayload, extractRefVersion, extractAuthor, } from "@hyper-hyper-space/hhs3_mvt";
export { formatOpVoidDetail } from "./op_void.js";
export function isVoidCheckable(payload) {
    if (isRefAdvancePayload(payload))
        return true;
    const p = payload;
    return p['action'] === 'row' || p['action'] === 'bundle';
}
// Table-scope variant: TableScope.unwrapPayload surfaces the inner row op
// (insert/update/delete) or a 'rows' slice (unwrapped bundle/create seed),
// never the group-level 'row'/'bundle' envelope. Genesis-derived 'rows' still
// resolves to OK via diagnoseEntryVoided's create-fiat path.
export function isVoidCheckableTableOp(payload) {
    if (isRefAdvancePayload(payload))
        return true;
    const p = payload;
    return p['action'] === 'insert'
        || p['action'] === 'update'
        || p['action'] === 'delete'
        || p['action'] === 'rows';
}
function bindingNameFor(host, refId) {
    for (const [name, id] of Object.entries(host.getBindings())) {
        if (id === refId)
            return name;
    }
    return undefined;
}
export function describeGroupEntry(entry, host, voidBefore, voidAfter) {
    const payload = entry.payload;
    const base = {
        entry: entry.hash,
        kind: 'insert',
        voidBefore,
        voidAfter,
    };
    if (isRefAdvancePayload(payload)) {
        const refId = payload.refId;
        const refVersion = [...extractRefVersion(payload)];
        const author = extractAuthor(payload);
        if (refId === host.getSchemaRef()) {
            return Object.assign(Object.assign(Object.assign({}, base), { kind: 'schema-deploy', refVersion }), (author !== undefined ? { author } : {}));
        }
        const binding = bindingNameFor(host, refId);
        return Object.assign(Object.assign(Object.assign(Object.assign({}, base), { kind: 'observe', refVersion }), (binding !== undefined ? { binding } : {})), (author !== undefined ? { author } : {}));
    }
    if (payload['action'] === 'row') {
        const envelope = payload;
        const op = envelope.op;
        return Object.assign(Object.assign(Object.assign({}, base), { kind: op.action, table: envelope.table, rowId: op.rowId }), (op.author !== undefined ? { author: op.author } : {}));
    }
    if (payload['action'] === 'bundle') {
        const bundle = payload;
        const writes = bundle.writes.map((w) => {
            const op = w.op;
            return { table: w.table, action: op.action, rowId: op.rowId };
        });
        writes.sort((a, b) => a.table.localeCompare(b.table)
            || a.rowId.localeCompare(b.rowId)
            || (a.action < b.action ? -1 : a.action > b.action ? 1 : 0));
        return Object.assign(Object.assign({}, base), { kind: 'bundle', writes });
    }
    return base;
}
export function computeOpVerdictFlips(host, entries, start, end) {
    return __awaiter(this, void 0, void 0, function* () {
        const changes = [];
        for (const entry of entries) {
            if (!isVoidCheckable(entry.payload))
                continue;
            const voidBefore = yield host.isEntryVoided(entry.hash, start);
            const voidAfter = yield host.isEntryVoided(entry.hash, end);
            if (voidBefore === voidAfter)
                continue;
            const voidHorizon = voidAfter ? 'end' : 'start';
            const horizon = voidAfter ? end : start;
            const reason = yield host.explainEntryVoided(entry.hash, horizon);
            changes.push(Object.assign(Object.assign(Object.assign({}, describeGroupEntry(entry, host, voidBefore, voidAfter)), { voidHorizon }), (reason !== undefined ? { reason } : {})));
        }
        changes.sort((a, b) => a.entry.localeCompare(b.entry));
        return changes;
    });
}
